import React, { useState, useEffect } from 'react';
import Card from '@mui/material/Card';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {
  showBackdropLoader,
  useNotification,
  dropDownSameOptions,
  getButtonSpinner,
  truncateTooltipText,
  clearSpace,
  onRedirect,
} from 'utils/commonUtils';
import {
  generateAttendanceTabs,
  getGradeValue,
  renderStudentTrackerTagsCell,
} from 'layouts/applications/attendance/utils/attendanceUtils';
import {
  getTrackerColumns,
  getTrackerGradeLevel,
  getTrackers,
} from 'layouts/studentTracking/services/studentTrackerApi';
import { useForm } from 'react-hook-form';
import { renderStudentTrackerUpdateDialog } from './StudentTrackerUpdateDialog';
import FormInputDropdown from 'components/Common/Forms/FormInputDropDown';
import Grid from '@mui/material/Grid';
import { useDebouncedCallback } from 'use-debounce';
import {
  generateTrackerTabPanel,
  renderStudentTrackerCell,
  getStudentTrackerCellClass,
  getHideUnHideColumns,
} from 'layouts/studentTracking/utils/studentTrackerUtils';
import {
  setTrackerId,
  setStudentType,
  setInstructor,
  setCourse,
  setTabValue,
  resetStudentTrackerFilter,
} from 'layouts/studentTracking/store/studentTrackerFilterSlice/studentTrackerFilterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getStudentProfileFilterOptions } from 'layouts/studentManagement/services/studentProfileApi';
import { setStudentProfileFilterOptions } from 'layouts/studentManagement/store/studentProfileFilterOptionsSlice/studentProfileFilterOptionsSlice';
import Typography from '@mui/material/Typography';
import { HeadingFormGrid, TypographyLink, LightTooltip, SchoolTypes } from 'components/Ui/styled';
import HideColumnDialog from './HideColumnDialog';
import { getTracker } from 'layouts/studentTracking/services/studentTrackerApi';
import { FormInputText } from 'components/Common/Forms/FormTextField';
import { setTrackerStatus } from 'layouts/studentTracking/store/trackerStatusSlice/trackerStatusSlice';
import { canManageStudentTrackersPermissionMessage } from 'constants';
import { usePermissionCustomHook } from 'layouts/users/userPermissions/utils/permissionsUtils';
import { getSearchStudentDetails } from 'layouts/students/service/studentsApi';
import { getStudentData } from 'layouts/students/store/studentDetailsSlice';
import { useNavigate } from 'react-router-dom';
import { renderStudentTagEditDialog } from 'layouts/applications/attendance/StudentTagEditDialog';
import {
  TabWrapperBox,
  AttendanceButtonYellow,
  AttendanceButtonOrange,
  AttendanceButtonBlue,
} from 'layouts/applications/attendance/Attendance.styled';
import IepTeacher from 'layouts/applications/attendance/IepTeacher';
import { getStudentDetails } from 'layouts/students/service/studentsApi';
import { setStudentProfileDetails } from 'layouts/studentManagement/store/studentProfileDetailsSlice/studentProfileDetailsSlice';
import StudentProfileDialog from 'layouts/students/StudentProfileDialog';

const StudentTrackerComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    setOpenNotification,
    setNotificationMessage,
    handleErrorResponse,
    NotificationPopup,
  } = useNotification();

  const [isLoading, setIsLoading] = useState(false);
  const [hideColumnDialog, setHideColumnDialog] = useState(false);
  const [hideColumns, setHideColumns] = useState([]);
  const [isHideColumnDialogLoading, setIsHideColumnDialogLoading] =
    useState(false);
  const [trackerDropDownList, setTrackerDropDownList] = useState([]);
  const [searchStudent, setSearchStudent] = useState(null);
  const [tableData, setTableData] = useState({ columns: [], rows: [] });
  const [clearFilterLoading, setClearFilterLoading] = useState(false);
  const [openStudentProfileDialog, setOpenStudentProfileDialog] =
    useState(false);
  const [studentProfileLoading, setStudentProfileLoading] = useState(false);

  const canManageStudentTrackers = usePermissionCustomHook(
    'CAN_MANAGE_STUDENT_TRACKERS'
  );
   const canUpdateTags = usePermissionCustomHook('CAN_UPDATE_TAGS');

  const studentTrackerFilters = useSelector(
    (state) => state.studentTrackerFilters
  );

  const { trackerId, studentType, instructor, course, tabValue } =
    studentTrackerFilters || {};

  const studentProfileFilterOptions = useSelector(
    (state) => state.studentProfileFilterOptions
  );

  const { allStudentTypes, allInstructors, allCourseNames } =
    studentProfileFilterOptions ?? [];

  const defaultValue = {
    searchStudent: searchStudent,
    trackerId: trackerId,
    tabValue: tabValue,
    studentType: studentType,
    instructor: instructor,
    course: course,
  };

  const disableHideUnhideButton = tableData.columns.length < 1 || !trackerId;

  const useFunction = useForm({
    mode: 'onChange',
    defaultValues: defaultValue,
  });

  const { control, reset, register } = useFunction;

  useEffect(() => {
    fetchTrackerOptions();
    fetchStudentProfileFilterOptionsData();
  }, []);

  const fetchTrackerOptions = async () => {
    try {
      const trackerOptions = await getTrackers();
      setTrackerDropDownList(trackerOptions?.trackers);
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const fetchStudentProfileFilterOptionsData = async () => {
    try {
      const studentProfileFilterOptionsData =
        await getStudentProfileFilterOptions();
      dispatch(setStudentProfileFilterOptions(studentProfileFilterOptionsData));
    } catch (error) {
      handleErrorResponse(error?.response?.data?.message);
    }
  };

  const trackerDropDownOptions = trackerDropDownList?.map((item) => ({
    id: item.trackerId,
    value: truncateTooltipText(item.name),
  }));

  const handleHideColumns = async () => {
    if (canManageStudentTrackers) {
      try {
        setIsHideColumnDialogLoading(true);
        const tracker = await getTracker(trackerId);
        const gradeValue = getGradeValue(tabValue);
        const hiddenColumnsForGrade =
          tracker?.hiddenColumnsByGrade[gradeValue] || [];
        const hideUnHideColumns = getHideUnHideColumns(
          tracker?.columns,
          hiddenColumnsForGrade
        );
        setHideColumns(hideUnHideColumns);
        setIsHideColumnDialogLoading(false);
        setHideColumnDialog(true);
      } catch (error) {
        setIsHideColumnDialogLoading(false);
        handleErrorResponse(error?.response?.data?.message);
      }
      return;
    }
    handleErrorResponse(canManageStudentTrackersPermissionMessage);
  };

  const handleStudentRedirect = async (studentId) => {
    onRedirect(
      setIsLoading,
      getSearchStudentDetails,
      studentId,
      dispatch,
      getStudentData,
      navigate,
      '/student-management/student-search/student-profile',
      setOpenNotification,
      setNotificationMessage
    );
  };

  const handleStudentProfile = async (params) => {
    try {
      setStudentProfileLoading(true);
      const { row } = params;
      const { studentNumber } = row.studentData;
      const studentData = await getStudentDetails(studentNumber);
      dispatch(getStudentData(studentData));
      try {
        const studentNoteInfoData = await getSearchStudentDetails(
          studentNumber
        );
        dispatch(setStudentProfileDetails(studentNoteInfoData));
      } catch (error) {
        setOpenNotification(true);
        setNotificationMessage(error.response.data.message);
      }
      setStudentProfileLoading(false);
      setOpenStudentProfileDialog(true);
    } catch (error) {
      setOpenNotification(true);
      setNotificationMessage(error.response.data.message);
      setStudentProfileLoading(false);
    }
  };

  const getTableData = async () => {
    setClearFilterLoading(true);
    const gradeValue = getGradeValue(tabValue);
    const trackerColumns = await getTrackerColumns(trackerId, gradeValue);
    var columns = [
      {
        headerName: 'Student ID',
        field: 'studentId',
        width: 140,
        renderCell: (params) => {
          const studentId = params.row.studentId;
          return (
            <TypographyLink
              variant="h6"
              onClick={() => handleStudentRedirect(studentId)}
              data-testid="studentIdValue"
            >
              {studentId}
            </TypographyLink>
          );
        },
      },
      {
        headerName: 'First Name',
        field: 'firstName',
        width: 150,
        renderCell: (params) => {
          const studentName = params.row.studentData.firstName;
          return <Typography variant="base">{studentName}</Typography>;
        },
      },
      {
        headerName: 'Last Name',
        field: 'lastName',
        width: 225,
        valueGetter: (params) => {
          const lastName = params.row.studentData.lastName;
          return `${lastName}`;
        },
        renderCell: (params) => {
          const studentId = params.row.studentData.studentNumber;
          const studentName = params.row.studentData.lastName;
          return (
            <LightTooltip
              title={<IepTeacher studentId={studentId} />}
              placement="top"
            >
              <TypographyLink
                variant="h6"
                data-testid='lastnameValue'
                onClick={() => handleStudentProfile(params)}
              >
                {studentName}
              </TypographyLink>
            </LightTooltip>
          );
        },
        cellClassName: (params) =>
          params.row.studentData.schoolId == 59952
            ? 'gameChangers'
            : params.row.studentData.schoolId == 92660
            ? 'rapa'
            : params.row.studentData.schoolId == 59953
            ? 'daySchool'
            : '',
      },
      {
        headerName: 'Tags',
        field: 'tags',
        width: 120,
        renderCell: (params) => renderStudentTrackerTagsCell(params),
        renderEditCell: (params) =>
          renderStudentTagEditDialog(params, () => {}, getTableData),
        editable: canUpdateTags,
        align: 'center',
      },
    ];
    trackerColumns?.columns?.map((item) => {
      return columns.push({
        headerName: item,
        field: item,
        width: 200,
        align: 'center',
        cellClassName: (params) => getStudentTrackerCellClass(params, item),
        renderEditCell: (params) =>
          renderStudentTrackerUpdateDialog(params, getTableData),
        renderCell: (params) => renderStudentTrackerCell(params, item),
        editable: true,
      });
    });
    try {
      let students;
      setIsLoading(true);
      students = await getTrackerGradeLevel(
        trackerId,
        gradeValue,
        studentType,
        instructor,
        course,
        clearSpace(searchStudent)
      );
      setIsLoading(false);
      const rows = Object.entries(students.data).map(([key, item]) => {
        return {
          ...item,
          id: key,
        };
      });
      setTableData({ columns: columns, rows: rows });
      setClearFilterLoading(false);
    } catch (error) {
      setOpenNotification(true);
      setNotificationMessage(error?.response?.data?.message);
      setIsLoading(false);
      setClearFilterLoading(false);
    }
  };

  useEffect(() => {
    if (trackerId) {
      getTableData();
    } else {
      setTableData({ columns: [], rows: [] });
    }
  }, [trackerId, tabValue, studentType, instructor, course, searchStudent]);

  const handleTabChange = (event, newTabValue) => {
    dispatch(setTabValue(newTabValue));
  };

  useEffect(() => {
    reset(defaultValue);
  }, [
    defaultValue.trackerId,
    defaultValue.tabValue,
    defaultValue.studentType,
    defaultValue.instructor,
    defaultValue.course,
    defaultValue.searchStudent,
  ]);

  useEffect(() => {
    if (trackerId && trackerDropDownList) {
      const statusOptions = trackerDropDownList?.find(
        (item) => trackerId === item.trackerId
      )?.statusOptions;
      dispatch(setTrackerStatus(statusOptions));
    }
  }, [trackerId, trackerDropDownList]);

  const handleStudentSearch = useDebouncedCallback((e) => {
    setSearchStudent(e.target.value);
  }, 500);

  const handleClearFilters = () => {
    setSearchStudent(null);
    dispatch(resetStudentTrackerFilter());
  };

  return (
    <>
      <Card>
        <Box mt={2}></Box>
        <HeadingFormGrid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <FormInputDropdown
              name="trackerId"
              control={control}
              options={trackerDropDownOptions}
              defaultValue={defaultValue?.trackerId}
              label="Trackers"
              register={register('trackerId', {
                onChange: (e) => dispatch(setTrackerId(e.target.value)),
              })}
              filter
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormInputDropdown
              name="studentType"
              control={control}
              options={dropDownSameOptions(allStudentTypes)}
              defaultValue={defaultValue?.studentType}
              label="Student Type"
              register={register('studentType', {
                onChange: (e) => dispatch(setStudentType(e.target.value)),
              })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormInputDropdown
              name="instructor"
              control={control}
              options={dropDownSameOptions(allInstructors)}
              defaultValue={defaultValue?.instructor}
              label="Instructor"
              register={register('instructor', {
                onChange: (e) => dispatch(setInstructor(e.target.value)),
              })}
              filter
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormInputDropdown
              name="course"
              control={control}
              options={dropDownSameOptions(allCourseNames)}
              defaultValue={defaultValue?.course}
              label="Course"
              register={register('course', {
                onChange: (e) => dispatch(setCourse(e.target.value)),
              })}
              filter
            />
          </Grid>
        </HeadingFormGrid>
        <HeadingFormGrid container spacing={2}>
          <Grid item xs={12} sm={4}>
            {trackerId && (
              <FormInputText
                name="searchStudent"
                type="text"
                control={control}
                label="Search"
                placeholder="Search students"
                register={register('searchStudent', {
                  onChange: (e) => {
                    handleStudentSearch(e);
                  },
                })}
              />
            )}
          </Grid>
          <Grid item xs={12} sm={2}></Grid>
          <Grid item xs={12} sm={6} md={3}>
            {getButtonSpinner(
              false,
              handleClearFilters,
              'Clear Filters',
              clearFilterLoading
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {getButtonSpinner(
              false,
              handleHideColumns,
              'Hide/Un-hide Columns',
              disableHideUnhideButton
            )}
          </Grid>
        </HeadingFormGrid>
        <TabWrapperBox>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Student Tracker"
          >
            {generateAttendanceTabs()}
          </Tabs>
          {isLoading && showBackdropLoader(isLoading)}
          {generateTrackerTabPanel(tabValue, tableData, [], false)}
        </TabWrapperBox>
        <SchoolTypes component="span">
          <Stack direction="row" spacing={2}>
            <AttendanceButtonYellow variant="contained">
              Game Changers
            </AttendanceButtonYellow>
            <AttendanceButtonOrange variant="contained">
              Rapa
            </AttendanceButtonOrange>
            <AttendanceButtonBlue variant="contained">
              Day School
            </AttendanceButtonBlue>
          </Stack>
        </SchoolTypes>
      </Card>
      <HideColumnDialog
        trackerId={trackerId}
        hideColumnDialog={hideColumnDialog}
        setHideColumnDialog={setHideColumnDialog}
        hideColumns={hideColumns}
        getTableData={getTableData}
        tabValue={tabValue}
        isHideColumnDialogLoading={isHideColumnDialogLoading}
      />
      <StudentProfileDialog
        open={openStudentProfileDialog}
        setOpen={setOpenStudentProfileDialog}
        loading={studentProfileLoading}
      />
      <NotificationPopup />
    </>
  );
};

export default StudentTrackerComponent;
